import React, { useState, useContext } from 'react';
import {
  ShieldCheck,
  Search,
  Filter,
  RefreshCw,
  Download,
  Info,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { AuthContext } from '../../contexts/AuthContext';
import { useTeams } from '../../contexts/TeamsContext';
import { getApiConfiguration } from '../../api/utils';
import { NacsaApi, ComplianceChecklist, QuestionWithAnswer, AnswerFromJSON } from 'api-client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { TablePagination } from '../../components/ui/TablePagination';

// 1. Helper functions
function getNacsaMaturity(score: number) {
  if (score >= 75) {
    return {
      label: "Advanced",
      description: "The organisation has well established cyber security practices across the organisation and is actively pursuing continuous improvement.",
    };
  } else if (score >= 50) {
    return {
      label: "Intermediate",
      description: "The organisation has established and standardised cyber security practices and requires minor improvement.",
    };
  } else if (score >= 25) {
    return {
      label: "Basic",
      description: "The organisation has established cyber security practices and requires further improvement.",
    };
  } else if (score >= 0) {
    return {
      label: "Initial",
      description: "The organisation has minimal cyber security practices and requires major improvement.",
    };
  } else {
    return {
      label: "Unknown",
      description: "No global score found or out of range.",
    };
  }
}

// Calculate element progress based on questions
function calculateProgress(questions: (QuestionWithAnswer | InternalQuestionWithAnswer)[]): string {
  if (!questions.length) return "Not Started";
  
  const totalQuestions = questions.length;
  
  const answeredQuestions = questions.filter(q => {
    if (typeof q.answer === 'string') {
      const answer = q.answer ? AnswerFromJSON(q.answer) : null;
      return answer?.response && answer.response !== "none";
    } else if (typeof q.answer === 'object' && q.answer !== null) {
      return q.answer.response && q.answer.response !== "none";
    }
    return false;
  }).length;

  if (answeredQuestions === 0) return "Not Started";
  if (answeredQuestions === totalQuestions) return "Complete";
  return "In Progress";
}

// Helper function to safely get response from answer
function getResponseFromAnswer(answer: string | { response: string } | null): string {
  if (!answer) return "none";
  if (typeof answer === 'string') {
    const parsedAnswer = AnswerFromJSON(answer);
    return parsedAnswer?.response || "none";
  }
  return answer.response || "none";
}

// Custom interface for internal representation of questions with complex answer objects
interface InternalQuestionWithAnswer extends Omit<QuestionWithAnswer, 'answer'> {
  answer: string | { response: string };
}

// Custom interface for internal representation of checklist items
interface InternalComplianceChecklist extends Omit<ComplianceChecklist, 'questions' | 'progress'> {
  questions: InternalQuestionWithAnswer[];
  progress: string;
}

const NacsaChecklist = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  
  const [checklist, setChecklist] = useState<InternalComplianceChecklist[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });
  const [orgGlobalScore, setOrgGlobalScore] = useState<number>(0);
  const itemsPerPage = 10;

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAssessOpen, setIsAssessOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InternalComplianceChecklist | null>(null);

  const handleAnswerSubmit = React.useCallback(async (questionId: number, resp: string) => {
    if (!currentTeam?.slug || !token) return;
    setIsLoading(true);
    setError(null);

    try {
      const api = new NacsaApi(getApiConfiguration(token));
      const assessment = await api.nacsaAnswersCreate({
        teamSlug: currentTeam.slug,
        answer: {
          question: questionId,
          response: resp,
          elementScore: "0",
          globalScore: "0"
        }
      });
      
      if (assessment) {
        // Update global score if available
        if (assessment.globalScore !== undefined) {
          setOrgGlobalScore(Number(assessment.globalScore));
        }

        // Update both checklist and selected item
        setChecklist((prevChecklist) =>
          prevChecklist.map((item) => {
            if (item.questions.some((q) => q.id === questionId)) {
              const updatedQuestions = item.questions.map((q) =>
                q.id === questionId ? { ...q, answer: { response: resp } } : q
              );
              return {
                ...item,
                score: assessment.elementScore,
                questions: updatedQuestions,
                progress: calculateProgress(updatedQuestions as QuestionWithAnswer[])
              };
            }
            return item;
          })
        );

        setSelectedItem((prevItem) => {
          if (!prevItem || !prevItem.questions.some((q) => q.id === questionId)) {
            return prevItem;
          }
          const updatedQuestions = prevItem.questions.map((q) =>
            q.id === questionId ? { ...q, answer: { response: resp } } : q
          );
          return {
            ...prevItem,
            score: assessment.elementScore,
            questions: updatedQuestions,
            progress: calculateProgress(updatedQuestions as QuestionWithAnswer[])
          };
        });
      }
    } catch (err) {
      console.error("Error updating checklist answer:", err);
      setError("Failed to update answer");
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token]);

  const fetchChecklist = React.useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new NacsaApi(getApiConfiguration(token));
      const response = await api.nacsaComplianceChecklistList({
        teamSlug: currentTeam.slug,
        page: currentPage,
      });

      setChecklist(response.results.map((item) => ({
        ...item,
        questions: item.questions.map((question) => {
          const answerObj = question.answer ? AnswerFromJSON(question.answer) : null;
          return {
            ...question,
            answer: answerObj ? { response: answerObj.response } : { response: "none" }
          };
        }),
      })));
      setPaginationInfo({
        count: response.count,
        next: String(response.next),
        previous: String(response.previous)
      });

      // 3. If the API includes a global score in the results:
      if (response.results?.length > 0 && response.results[0].globalScore !== undefined) {
        setOrgGlobalScore(Number(response.results[0].globalScore));
      }

    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError('Failed to load compliance checklist');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery]);

  React.useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  // 4. Derive label/description for the NACSA posture
  const { label: orgLabel, description: orgDescription } = getNacsaMaturity(orgGlobalScore);

  return (
    <div className="space-y-6">
      {/* 
        NEW SECTION: Organization NACSA Posture 
        Displayed ABOVE the table
      */}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ShieldCheck className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">NACSA Compliance Checklist</h1>
            <p className="text-sm text-gray-400">Track and manage compliance controls</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={fetchChecklist}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" disabled>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gradient-to-br from-[#0F1629] to-[#1a2236] p-6 rounded-2xl border border-gray-800/30 shadow-xl">
        {/* Score Section */}
        <div className="space-y-4">
          <div className="flex items-baseline gap-2">
            <h2 className="text-2xl font-semibold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
              NACSA Posture Score
            </h2>
            <div className="h-8 w-[2px] bg-gray-700/50 mx-2" />
            <span className="text-3xl font-bold text-white">{Number(orgGlobalScore).toFixed(2)}%</span>
          </div>
          <div className="flex items-center gap-3">
            <Badge
              variant={
                orgLabel === "Advanced"
                  ? "success"
                  : orgLabel === "Intermediate"
                    ? "warning"
                    : orgLabel === "Basic"
                      ? "default"
                      : "destructive"
              }
              className="px-3 py-1 text-sm font-medium"
            >
              {orgLabel}
            </Badge>
            <span className="text-gray-400 text-sm">|</span>
            <span className="text-gray-400 text-sm">{currentTeam?.name}</span>
          </div>
        </div>

        {/* Description Section */}
        <div className="flex items-center">
          <div className="p-4 bg-[#1c2943]/50 rounded-xl border border-gray-700/30">
            <p className="text-gray-300 text-sm leading-relaxed">
              {orgDescription}
            </p>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <input
            type="text"
            placeholder="Search controls..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-400">Loading checklist...</p>
        </div>
      )}

      {/* Controls Table */}
      <div className="bg-[#0A0F1C] rounded-xl border border-gray-800/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Element</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Domain</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Category</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Maturity</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Score</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Progress</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Last Assessed</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-400">
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    <span>Loading elements...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : checklist.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-400">
                  No elements found
                </td>
              </tr>
            ) : (
              checklist.map((item) => (
                <tr key={item.id} className="group hover:bg-gray-800/30">
                  <td className="px-6 py-4 text-white">{item.name}</td>
                  <td className="px-6 py-4 text-gray-300">
                    <div className="flex flex-wrap gap-1">
                      {item.categoryDomains.map((domain, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {domain}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-300">{item.categoryName}</td>
                  <td className="px-6 py-4">
                    <Badge
                      variant={
                        Number(item.score) === 3 ? 'success' :
                          Number(item.score) === 2 ? 'warning' :
                            Number(item.score) === 1 ? 'default' :
                              'destructive'
                      }
                    >
                      {Number(item.score) === 3
                        ? 'Advanced'
                        : Number(item.score) === 2
                          ? 'Intermediate'
                          : Number(item.score) === 1
                            ? 'Basic'
                            : 'Initial'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-gray-300">{item.score}</td>
                  <td className="px-6 py-4">
                    <Badge
                      variant={
                        item.progress === "Complete"
                          ? 'success'
                          : item.progress === "In Progress"
                            ? 'warning'
                            : 'destructive'
                      }
                      className="text-xs"
                    >
                      {item.progress}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-gray-300">
                    {new Date(item.lastAssessed).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedItem(item);
                          setIsDetailsOpen(true);
                        }}
                      >
                        <Info className="h-4 w-4 mr-2" />
                        Details
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedItem(item);
                          setIsAssessOpen(true);
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Assess
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {!isLoading && !error && checklist.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            totalItems={paginationInfo.count || 0}
            itemsPerPage={itemsPerPage}
            paginationInfo={{
              count: paginationInfo.count || 0,
              next: paginationInfo.next,
              previous: paginationInfo.previous
            }}
            itemName="elements"
          />
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedItem?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge
                  variant={
                    Number(selectedItem?.score) === 3
                      ? "success"
                      : Number(selectedItem?.score) === 2
                        ? "warning"
                        : Number(selectedItem?.score) === 1
                          ? "default"
                          : "destructive"
                  }
                >
                  {Number(selectedItem?.score) === 3
                    ? "Advanced"
                    : Number(selectedItem?.score) === 2
                      ? "Intermediate"
                      : Number(selectedItem?.score) === 1
                        ? "Basic"
                        : "Initial"}
                </Badge>
                <div className="h-4 w-[1px] bg-gray-700" />
                <Badge
                  variant={
                    selectedItem?.progress === "Complete"
                      ? "success"
                      : selectedItem?.progress === "In Progress"
                        ? "warning"
                        : "destructive"
                  }
                >
                  {selectedItem?.progress}
                </Badge>
              </div>
            </div>

            <div className="p-4 bg-[#1A1F2E] rounded-lg">
              <h4 className="font-semibold text-gray-200 mb-2">Description</h4>
              <p className="text-sm text-gray-300">{selectedItem?.description}</p>
            </div>

            <div className="p-4 bg-[#1A1F2E] rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-gray-200">Maturity Level</h4>
                <Badge
                  variant={
                    Number(selectedItem?.score) === 3 ? 'success' :
                      Number(selectedItem?.score) === 2 ? 'warning' :
                        Number(selectedItem?.score) === 1 ? 'default' :
                          'destructive'
                  }
                >
                  {Number(selectedItem?.score) === 3
                    ? 'Advanced'
                    : Number(selectedItem?.score) === 2
                      ? 'Intermediate'
                      : Number(selectedItem?.score) === 1
                        ? 'Basic'
                        : 'Initial'}
                </Badge>
              </div>
              <div className="text-sm text-gray-300 whitespace-pre-line">
                {Number(selectedItem?.score) === 3
                  ? selectedItem?.descriptionAdvanced
                  : Number(selectedItem?.score) === 2
                    ? selectedItem?.descriptionIntermediate
                    : Number(selectedItem?.score) === 1
                      ? selectedItem?.descriptionBasic
                      : selectedItem?.descriptionInitial}
              </div>
            </div>

            <div className="p-4 bg-[#1A1F2E] rounded-lg">
              <h4 className="font-semibold text-gray-200 mb-2">Assessment History</h4>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-300">Last Assessment Date</p>
                <p className="text-sm text-gray-400">
                  {selectedItem?.lastAssessed
                    ? new Date(selectedItem.lastAssessed).toLocaleDateString()
                    : 'Not yet assessed'}
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assessment Dialog */}
      <Dialog open={isAssessOpen} onOpenChange={setIsAssessOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              {selectedItem?.name} - Questions & Answers
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {selectedItem?.questions?.map((question, index) => {
              const answer = getResponseFromAnswer(question.answer);
  
              return (
                <div key={index} className="p-4 bg-[#1A1F2E] rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {answer === "yes" ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : answer === "no" ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <Info className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-grow">
                      <p className="text-sm text-gray-200">{question.text}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-400">
                          Answer:{" "}
                          <span className="font-medium capitalize">
                            {answer === "none"
                              ? "Not Answered"
                              : answer || "Not answered"}
                          </span>
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAnswerSubmit(question.id, 'yes')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${answer === 'yes'
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-700 hover:bg-green-500/20 text-gray-300'
                              }`}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => handleAnswerSubmit(question.id, 'no')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${answer === 'no'
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-700 hover:bg-red-500/20 text-gray-300'
                              }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};;

export default NacsaChecklist;
